import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { faCrosshairsSimple } from "@fortawesome/pro-light-svg-icons";

import type { OverviewTemperatures } from "@core/apis/shared";
import { SolidCard } from "@components";

import { KPICardGrid } from "../shared/KPICardGrid";

type OverviewTemperatureKpisProps = Readonly<{
  temperatures: OverviewTemperatures;
  isLoading: boolean;
}>;

export function OverviewTemperatureKpis({ temperatures, isLoading }: OverviewTemperatureKpisProps) {
  const { t } = useTranslation(["propai_properties", "propai"]);

  const kpiData = useMemo(() => {
    const formatValue = (value: number | null) => {
      if (isLoading) return { value: "...", valueFormat: "{value}" };
      if (typeof value === "number" && !isNaN(value)) {
        return { value: value.toFixed(1), valueFormat: "{value} °C" };
      }
      return { value: "N/A", valueFormat: "{value}" };
    };

    const targetTemp = formatValue(temperatures.targetTemperature);
    const currentTemp = formatValue(temperatures.currentAvgTemp);
    const avg7d = formatValue(temperatures.avgTemp7d);
    const avg24h = formatValue(temperatures.avgTemp24h);

    return [
      {
        id: "kpi-cell-target",
        title: t("target_temperature"),
        value: targetTemp.value,
        valueFormat: targetTemp.valueFormat,
        icon: faCrosshairsSimple,
        seriesData: [],
      },
      {
        id: "kpi-cell-current",
        title: t("current_average_indoor_temperature"),
        value: currentTemp.value,
        valueFormat: currentTemp.valueFormat,
        seriesData: [],
      },
      {
        id: "kpi-cell-7d",
        title: t("overview_last_7_days", { ns: "propai" }),
        value: avg7d.value,
        valueFormat: avg7d.valueFormat,
        seriesData: temperatures.sparkline7d,
        seriesType: "area" as const,
      },
      {
        id: "kpi-cell-24h",
        title: t("overview_last_24_hours", { ns: "propai" }),
        value: avg24h.value,
        valueFormat: avg24h.valueFormat,
        seriesData: temperatures.sparkline24h,
        seriesType: "area" as const,
      },
    ];
  }, [temperatures, isLoading, t]);

  return (
    <SolidCard title={t("average_indoor_temperature", { ns: "propai" })}>
      <KPICardGrid kpiData={kpiData} loading={isLoading} columns={4} />
    </SolidCard>
  );
}
